import type {
  CurrentEpisodeResponse,
  TaskListResponse,
} from '../../services/api/types.gen';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import {
  classifyEpisodeBoundaryReschedule,
  type EpisodeBoundary,
} from '../../utils/episodeBoundaryVisits';

// The task table is a CSS grid, so the header and every row must declare the
// same track list. Kept here as two whole literals rather than one interpolated
// string: Tailwind scans source text, so a template built at runtime would
// never be emitted. EMR-325 adds the narrow EVV track before Actions, and only
// for users who can see EVV at all.
export const TASK_GRID_LG =
  'lg:grid-cols-[36px_minmax(150px,1fr)_minmax(170px,1fr)_minmax(110px,0.6fr)_minmax(110px,0.6fr)_minmax(200px,1.2fr)_minmax(156px,auto)]';
export const TASK_GRID_LG_WITH_EVV =
  'lg:grid-cols-[36px_minmax(150px,1fr)_minmax(170px,1fr)_minmax(110px,0.6fr)_minmax(110px,0.6fr)_minmax(200px,1.2fr)_44px_minmax(156px,auto)]';

// Floor width of the lg task table, applied to the header and the row list so
// the grid grows to fit its tracks inside the scroller instead of overflowing
// them. Sum of every track minimum, plus gap-3 (12px) between tracks and px-3
// (24px) of row padding; recompute when TASK_GRID_LG changes:
//   36+150+170+110+110+200+156 = 932, + 6 gaps (72) + 24 = 1028
//   with the 44px EVV track: 976, + 7 gaps (84) + 24 = 1084
export const TASK_TABLE_MIN_W_LG = 'lg:min-w-[1028px]';
export const TASK_TABLE_MIN_W_LG_WITH_EVV = 'lg:min-w-[1084px]';

export const LOCKED_TASK_ASSIGNEE_TEXT_CLASS =
  'flex h-7 min-w-0 items-center px-2 text-xs text-muted-foreground';

export const LOCKED_TASK_DATE_TEXT_CLASS =
  'flex h-6 max-w-[220px] items-center pr-1.5 text-xs text-muted-foreground';

// Mirrors the backend's MAX_BATCH_PRINT_TASKS ceiling. The frontend caps
// selection here so the 422 backstop is never the primary UX.
export const MAX_BATCH_SELECTED = 30;

export type AddTaskTargetEpisode = {
  id: string;
  start_date?: string | null;
  end_date?: string | null;
  agency_clinic_id?: string | null;
  episode_physician_id?: string | null;
  // Undefined when the status is unknown (the current-episode fallback does not
  // return one). Gates that read this must fail open so an unknown status never
  // blocks a task the backend would have accepted.
  status?: string | null;
};

// Visits can only be scheduled on an active episode; the backend rejects the
// rest with a 400. Orders, misc tasks and communications have no such gate, so
// they stay available on a completed or cancelled episode.
export const episodeBlocksNewVisits = (
  episode: AddTaskTargetEpisode | null
): boolean => !!episode?.status && episode.status !== 'active';

export const currentEpisodeToAddTaskTarget = (
  episode: CurrentEpisodeResponse | null
): AddTaskTargetEpisode | null => {
  if (!episode?.episode_id) return null;
  return {
    id: episode.episode_id,
    start_date: episode.start_date,
    end_date: episode.end_date,
    agency_clinic_id: episode.agency_clinic_id,
    episode_physician_id: episode.physician_id,
  };
};

export const clampDateYmdToEpisode = (
  candidateYmd: string,
  episode: AddTaskTargetEpisode | null
): string => {
  if (!candidateYmd || !episode) return candidateYmd;
  if (episode.start_date && candidateYmd < episode.start_date) {
    return episode.start_date;
  }
  if (episode.end_date && candidateYmd > episode.end_date) {
    return episode.end_date;
  }
  return candidateYmd;
};

export const dateFromYmd = (ymd: string): Date | undefined =>
  parseISOToLocalDate(ymd);

/**
 * Which active-episode boundary this task's visit currently sits on, or null.
 * Used both to offer moving the episode date on reschedule and to relax the
 * date picker so a boundary visit can be moved outside the current window.
 */
export const activeEpisodeBoundaryForVisit = (
  task: TaskListResponse
): EpisodeBoundary | null => {
  if (task.episode_status !== 'active') {
    return null;
  }
  return classifyEpisodeBoundaryReschedule({
    visitType: task.visit_type,
    currentScheduledDate: task.scheduled_date,
    episodeStartDate: task.episode_start_date,
    episodeEndDate: task.episode_end_date,
  });
};
